import { useEffect, useState } from 'react';
import { Table, Tag } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { pressureAPI, type PressurePoint } from '../../services/api';
import DepthProfile, { type Track } from '../mechanics/DepthProfile';
import { MTH } from '../mechanics/mechShared';

type Props = {
  mode: 'pressure' | 'window';
};

export default function PressureWindowPage({ mode }: Props) {
  const { currentProject } = useProjectStore();
  const [data, setData] = useState<PressurePoint[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentProject?.id) return;
    setLoading(true);
    pressureAPI
      .getProfile(currentProject.id)
      .then(({ data: response }) => {
        const rows = (Array.isArray(response) ? response : response?.results ?? []) as PressurePoint[];
        rows.sort((a, b) => a.depth - b.depth);
        setData(rows);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [currentProject?.id]);

  if (!currentProject) {
    return <div style={{ padding: 40, color: MTH.textSec }}>请先选择项目</div>;
  }

  const tracks: Track<PressurePoint>[] =
    mode === 'window'
      ? [
          {
            title: '安全窗口压力', unit: 'MPa', min: 0, max: 120,
            series: [
              { label: 'P_low', color: '#2874A6', accessor: (p) => p.safe_window_lower_pressure },
              { label: 'P_up', color: '#C2533A', accessor: (p) => p.safe_window_upper_pressure },
            ],
          },
          {
            title: '当量密度', unit: 'g/cm³', min: 0, max: 3,
            series: [
              { label: 'EMW_low', color: '#2874A6', accessor: (p) => p.safe_window_lower_emw },
              { label: 'EMW_up', color: '#C2533A', accessor: (p) => p.safe_window_upper_emw },
            ],
          },
        ]
      : [
          {
            title: '三压力剖面', unit: 'MPa', min: 0, max: 120,
            series: [
              { label: 'Pp', color: '#2874A6', accessor: (p) => p.pore_pressure },
              { label: 'Pc', color: '#B8860B', accessor: (p) => p.collapse_pressure },
              { label: 'Pf', color: '#C2533A', accessor: (p) => p.fracture_pressure },
            ],
          },
        ];

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: MTH.bg }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${MTH.headerBorder}` }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: MTH.text, margin: 0 }}>
          {mode === 'window' ? '安全泥浆密度窗口（工作台发布回读）' : '坍塌 / 破裂压力剖面（工作台发布回读）'}
        </h2>
        <span style={{ fontSize: 12, color: MTH.textMuted }}>
          {loading ? '加载中…' : `已读取 PressureProfile ${data.length} 行`}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: '4px 8px' }}>
        {!loading && data.length === 0 ? (
          <div style={{ padding: 40, color: MTH.textMuted, textAlign: 'center' }}>
            无压力剖面数据 — 请先在地质力学工作台完成计算并发布
          </div>
        ) : (
          <DepthProfile points={data} tracks={tracks} />
        )}
      </div>
      <Table<PressurePoint>
        size="small"
        style={{ maxHeight: 320, overflow: 'auto' }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        dataSource={data}
        rowKey="id"
        columns={[
          { title: '深度', dataIndex: 'depth', width: 90 },
          { title: '基准', dataIndex: 'depth_reference', width: 80 },
          { title: 'Pp', dataIndex: 'pore_pressure', width: 90, render: (v) => v ?? '-' },
          { title: 'Pc', dataIndex: 'collapse_pressure', width: 90, render: (v) => v ?? '-' },
          { title: 'Pf', dataIndex: 'fracture_pressure', width: 90, render: (v) => v ?? '-' },
          { title: '窗口下限', dataIndex: 'safe_window_lower_pressure', width: 100, render: (v) => v ?? '-' },
          { title: '窗口上限', dataIndex: 'safe_window_upper_pressure', width: 100, render: (v) => v ?? '-' },
          {
            title: '风险',
            dataIndex: 'safe_window_risk_code',
            width: 80,
            render: (v) =>
              v == null ? '-' : (
                <Tag color={v === 4 ? 'red' : v === 3 ? 'orange' : v === 2 ? 'gold' : 'green'}>
                  {v}
                </Tag>
              ),
          },
        ]}
      />
    </div>
  );
}
